import { type DBSchema, type IDBPDatabase, openDB } from "idb";

type HashesIndexMetaFile = {
	version: string;
	dataPath: string; // absolute web path, e.g. "/hashes_index.json"
	totalHashes?: number;
	collisions?: number;
	generatedAt?: string;
};

interface HashesIndexDB extends DBSchema {
	meta: {
		key: string;
		value: { key: string; value: string; updatedAt: number };
	};
	hashes: {
		key: string;
		value: { hash: string; html: string };
	};
}

const DB_NAME = "hashes_index";
const DB_VERSION = 1;

const STORE_META = "meta";
const STORE_HASHES = "hashes";

let dbPromise: Promise<IDBPDatabase<HashesIndexDB>> | null = null;
let ensurePromise: Promise<void> | null = null;

function canUseIndexedDB(): boolean {
	// `indexedDB` is a global in browsers that support it.
	return typeof indexedDB !== "undefined";
}

function getPrefixPath(): string {
	// Matches the convention used elsewhere for VSCode Live Server.
	return window.location.host.includes(":") ? "/public" : "";
}

function joinWebPath(prefix: string, absolutePath: string): string {
	if (!absolutePath.startsWith("/")) return `${prefix}/${absolutePath}`;
	return `${prefix}${absolutePath}`;
}

async function getDb(): Promise<IDBPDatabase<HashesIndexDB> | null> {
	if (!canUseIndexedDB()) return null;

	if (!dbPromise) {
		dbPromise = openDB<HashesIndexDB>(DB_NAME, DB_VERSION, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(STORE_META)) {
					db.createObjectStore(STORE_META, { keyPath: "key" });
				}
				if (!db.objectStoreNames.contains(STORE_HASHES)) {
					db.createObjectStore(STORE_HASHES, { keyPath: "hash" });
				}
			},
		});
	}

	try {
		return await dbPromise;
	} catch {
		// If IndexedDB fails (e.g., private mode restrictions), disable caching.
		return null;
	}
}

async function fetchJson<T>(url: string): Promise<T> {
	const res = await fetch(url, { cache: "no-cache" });
	if (!res.ok) throw new Error(`Fetch failed: ${url} (${res.status})`);
	return (await res.json()) as T;
}

async function ensureHashesIndexCacheImpl(): Promise<void> {
	const db = await getDb();
	if (!db) return;

	const prefixPath = getPrefixPath();

	let meta: HashesIndexMetaFile;
	try {
		meta = await fetchJson<HashesIndexMetaFile>(
			joinWebPath(prefixPath, "/hashes_index.meta.json"),
		);
	} catch (err) {
		console.warn("[hashes_index] failed to fetch meta", err);
		return;
	}

	// Already up-to-date?
	const existing = await db.get(STORE_META, "version");
	if (existing?.value === meta.version) {
		return;
	}

	let data: Record<string, string>;
	try {
		const url = joinWebPath(prefixPath, meta.dataPath);
		data = await fetchJson<Record<string, string>>(url);
	} catch (err) {
		console.warn("[hashes_index] failed to fetch data", err);
		return;
	}

	const tx = db.transaction([STORE_META, STORE_HASHES], "readwrite");
	const metaStore = tx.objectStore(STORE_META);
	const hashesStore = tx.objectStore(STORE_HASHES);

	await metaStore.clear();
	await hashesStore.clear();

	await metaStore.put({
		key: "version",
		value: meta.version,
		updatedAt: Date.now(),
	});

	const entries = Object.entries(data);
	const chunkSize = 500;

	for (let i = 0; i < entries.length; i += chunkSize) {
		const chunk = entries.slice(i, i + chunkSize);
		await Promise.all(
			chunk.map(([hash, html]) => hashesStore.put({ hash, html })),
		);
	}

	await tx.done;

	console.log("[hashes_index] refreshed", {
		version: meta.version,
		hashes: entries.length,
	});
}

/**
 * Page-load bootstrap:
 * - Fetches hashes_index.meta.json to get the latest "version" + data path.
 * - If IndexedDB already has that version under DB "hashes_index", it does nothing.
 * - Otherwise fetches hashes_index.json, clears the DB stores, and repopulates them.
 */
export async function ensureHashesIndexCache(): Promise<void> {
	if (!ensurePromise) ensurePromise = ensureHashesIndexCacheImpl();
	return ensurePromise;
}

export async function getHashesIndexHtml(hash: string): Promise<string | null> {
	const db = await getDb();
	if (!db) return null;

	const row = await db.get(STORE_HASHES, hash);
	return row?.html ?? null;
}
